import { Grid, TextField } from "@mui/material";
import { useTranslation } from "react-i18next";

import {
  DateInput,
  FormContainer,
  FormInput,
  SwitchInput,
  Treturn,
  useFetch,
} from "@4uhub/lib4uhub";
import { useCallback } from "react";
import {
  createSendLimitMonth,
  getSendLimitMonthById,
  updateSendLimitMonthById,
} from "../../../../../../services/sendLimitMonth.service";
import {
  sendLimitMonthsSchema,
  TSendLimitMonths,
} from "./SendLimitMonthsSchema";
import { TicketService } from "./TicketService";

const translationPath = "page.settings.crm.sendLimitMonths.";

const SendLimitMonths = () => {
  const { t } = useTranslation();

  const { sendRequest: save, loading: sLoading } =
    useFetch(createSendLimitMonth);

  const { sendRequest: update, loading: uLoading } = useFetch(
    updateSendLimitMonthById
  );

  const { sendRequest: get, loading: gLoading } = useFetch(
    getSendLimitMonthById
  );

  const handleSave = (v: TSendLimitMonths) => {
    return save({
      ...v,
      ticketServiceId: v.ticketService.id,
      startDate: v.startDate.toISOString(),
      endDate: v.endDate.toISOString(),
      valueLimit: Number(v.valueLimit),
      alertPercentLimit: v.alertPercentLimit
        ? Number(v.alertPercentLimit)
        : null,
    });
  };

  const updateHandler = (v: TSendLimitMonths, id: string) =>
    update({
      item: {
        ...v,
        id: id,
        ticketServiceId: v.ticketService.id,
        startDate: v.startDate.toISOString(),
        endDate: v.endDate.toISOString(),
        valueLimit: Number(v.valueLimit),
        alertPercentLimit: v.alertPercentLimit
          ? Number(v.alertPercentLimit)
          : null,
      },
      id: id,
    });

  const getHandler = useCallback(
    async (id: string): Promise<Treturn<TSendLimitMonths>> => {
      const { data, success } = await get(id);

      if (data) {
        const newData: TSendLimitMonths = {
          ...data,
          startDate: new Date(data.startDate),
          endDate: new Date(data.endDate),
          valueLimit: data.valueLimit.toString(),
          alertPercentLimit: data.alertPercentLimit?.toString(),
        };
        return { data: newData, success: true };
      }
      return { data, success };
    },
    [get]
  );

  const loading = sLoading || uLoading || gLoading;

  return (
    <FormContainer<TSendLimitMonths>
      saveFunction={handleSave}
      updateFunction={updateHandler}
      getFunction={getHandler}
      loading={loading}
      title={t(translationPath + "send_limit_months")}
      schema={sendLimitMonthsSchema}
      subtitle={t(translationPath + "new_send_limit_months")}
      subtitleWatchField="name"
      fields={() => (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={12} textAlign="end">
            <SwitchInput name="enable" label={t(translationPath + "enable")} />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormInput
              size="small"
              name="name"
              fullWidth
              label={t(translationPath + "name")}
            />
          </Grid>

          <TicketService />

          <Grid item xs={12} sm={6}>
            <DateInput
              name="startDate"
              label={t(translationPath + "start_date")}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <DateInput name="endDate" label={t(translationPath + "end_date")} />
          </Grid>

          <Grid item xs={12} sm={3}>
            <FormInput
              size="small"
              name="valueLimit"
              type="number"
              fullWidth
              label={t(translationPath + "quantity")}
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            <FormInput
              size="small"
              name="alertPercentLimit"
              type="number"
              fullWidth
              label={t(translationPath + "alert_percent_limit")}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormInput
              size="small"
              name="alertMail"
              fullWidth
              label={t(translationPath + "alert_mail")}
            />
          </Grid>

          <Grid item xs={12} sm={12}>
            <FormInput
              size="small"
              name="description"
              fullWidth
              multiline
              minRows={4}
              label={t(translationPath + "description")}
            />
          </Grid>
        </Grid>
      )}
    />
  );
};

export default SendLimitMonths;
